import React, { useState } from "react";
import { useTranslation } from "react-i18next";

function Timeline() {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set()); // Für mobile expandierbare Cards

  const events = t("timeline.events", { returnObjects: true });
  
  // Sortiere Events nach Jahr für bessere chronologische Darstellung
  const sortedEvents = [...events].sort((a, b) => a.year - b.year);

  // Teile Events für 3-Spalten-Layout auf (5 links, Rest rechts)
  const leftEvents = sortedEvents.slice(0, 5);
  const rightEvents = sortedEvents.slice(5);

  const handleIconClick = (year) => {
    setSelectedYear(year);
  };

  const handleCardToggle = (year) => {
    setExpandedCards((prev) => {
      // Wenn die Card bereits geöffnet ist, schließe sie
      if (prev.has(year)) {
        return new Set();
      }
      // Andernfalls schließe alle anderen und öffne nur diese Card
      return new Set([year]);
    });
  };

  const selectedEvent = events.find((event) => event.year === selectedYear);

  // Berechne Jahre seit dem Event (nur für Details-Sektion)
  const getYearsAgo = (year) => {
    const currentYear = new Date().getFullYear();
    return currentYear - year;
  };

  // Render-Funktion für eine Card
  const renderCard = (event, index, isMobile = false) => {
    // Bei Mobile: Card ist selected wenn sie expandiert ist
    // Bei Desktop: Card ist selected wenn sie in selectedYear ist
    const isExpanded = expandedCards.has(event.year);
    const isSelected = isMobile ? isExpanded : selectedYear === event.year;

    return (
      <div
        key={event.year}
        className={`group relative bg-slate-50 dark:bg-slate-900 rounded-lg border shadow-sm p-5 cursor-pointer transition-all duration-200 ${
          isSelected
            ? "border-primary dark:border-primary-light shadow-md ring-2 ring-primary/20 dark:ring-primary-light/20"
            : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md"
        }`}
        onClick={() => {
          if (isMobile) {
            handleCardToggle(event.year);
          } else {
            handleIconClick(event.year);
          }
        }}
      >
        {/* Subtile Timeline-Linie (nur visuell, nicht funktional) - nur auf Desktop */}
        {!isMobile && index < sortedEvents.length - 1 && (
          <div className="hidden xl:block absolute top-1/2 -right-2 w-4 h-0.5 bg-slate-200 dark:bg-slate-700 transform translate-x-full opacity-30"></div>
        )}

        <div className="flex items-center gap-4">
          {/* Runde Icons */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
            isSelected
              ? "bg-primary dark:bg-primary-light text-white shadow-lg"
              : "bg-primary/10 dark:bg-primary-light/20 text-primary dark:text-primary-light group-hover:bg-primary/20 dark:group-hover:bg-primary-light/30"
          }`}>
            <i className={`${event.icon} text-lg`}></i>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className={`text-base font-semibold mb-1.5 ${
              isSelected
                ? "text-primary dark:text-primary-light"
                : "text-slate-900 dark:text-slate-100"
            }`}>
              {event.title}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 leading-relaxed">
              {event.description}
            </p>
          </div>

          {/* Expand/Collapse Icon für Mobile */}
          {isMobile && (
            <div className="flex-shrink-0">
              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-slate-400 dark:text-slate-500 text-sm`}></i>
            </div>
          )}
        </div>

        {/* Expandierter Inhalt für Mobile */}
        {isMobile && isExpanded && (
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
            <div className="pt-3">
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                {event.details}
              </p>
            </div>

            {event.year < new Date().getFullYear() && (
              <div className="flex items-center gap-4 pt-2 text-xs text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5">
                  <i className="fas fa-history"></i>
                  Vor {getYearsAgo(event.year)} Jahren
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render-Funktion für Details-Box
  const renderDetailsBox = () => (
    <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden h-full flex flex-col">
      {selectedEvent ? (
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
              <i className={`${selectedEvent.icon} text-primary dark:text-primary-light text-xl`}></i>
            </div>
            
            <div className="flex-1 space-y-3 flex flex-col">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  {selectedEvent.title}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                  {selectedEvent.description}
                </p>
              </div>
              
              <div className="pt-3 border-t border-slate-200 dark:border-slate-700 flex-1">
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                  {selectedEvent.details}
                </p>
              </div>

              {selectedEvent.year < new Date().getFullYear() && (
                <div className="flex items-center gap-4 pt-2 text-xs text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <i className="fas fa-history"></i>
                    Vor {getYearsAgo(selectedEvent.year)} Jahren
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>
      ) : (
        <div className="text-center py-10">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-info-circle text-slate-400 dark:text-slate-500 text-xl"></i>
          </div>
          <p className="text-slate-600 dark:text-slate-400">
            {t("timeline.defaultDetailsMessage")}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <section className="pt-12 pb-8 bg-white dark:bg-slate-800 transition-colors" id="timeline">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("timeline.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
            {t("timeline.description")}
          </p>
        </div>

        {/* Mobile: Expandierbare Cards (1 Spalte) */}
        <div className="md:hidden space-y-4">
          {sortedEvents.map((event, index) => renderCard(event, index, true))}
        </div>

        {/* Tablet: 2 Spalten Grid mit Details unten (md bis xl) */}
        <div className="hidden md:block xl:hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {sortedEvents.map((event, index) => renderCard(event, index, false))}
          </div>
          {renderDetailsBox()}
        </div>

        {/* Desktop: 3 Spalten Layout mit Details in der Mitte (xl und größer) */}
        <div className="hidden xl:grid xl:grid-cols-3 xl:gap-6 xl:items-stretch">
          {/* Linke Spalte: Erste 5 Events */}
          <div className="space-y-4 flex flex-col">
            {leftEvents.map((event, index) => renderCard(event, index, false))}
          </div>

          {/* Mittlere Spalte: Details Box */}
          <div className="xl:col-span-1">
            {renderDetailsBox()}
          </div>

          {/* Rechte Spalte: Restliche Events */}
          <div className="space-y-4 flex flex-col">
            {rightEvents.map((event, index) => renderCard(event, index + 5, false))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Timeline;
